import { persistentAtom } from '@nanostores/persistent';
import { calculateCourseProgress } from '@klaviyo-mkt/components/molecules/unified-search-result-grid-item/utils';
import type { IResourceProgress , ICollectionProgress } from './learning-progress';

interface ILastViewedCourse {
  id: string;
  name: string;
  description: string;
  link: string;
  duration?: string;
  progress?: {
    completed_lesson_count: number;
    total_lesson_count: number;
    percentage: number;
  };
}

export interface ILastViewedCollection {
  id: string;
  name: string;
  description: string;
  link: string;
  collectionType?: ICollectionProgress['collectionType'];
  progress?: {
    completed_course_count: number;
    total_course_count: number;
    percentage: number;
  };
  lastViewedCourse: ILastViewedCourse;
  lastViewedAt: number;
}

export interface ILastViewedResource {
  id: string;
  name: string;
  description: string;
  link: string;
  resourceType: IResourceProgress['resourceType'];
  duration?: string;
  progress?: {
    completed_lesson_count: number;
    total_lesson_count: number;
    percentage: number;
  };
  lastViewedAt: number;
}

export const $lastViewedResource = persistentAtom<ILastViewedResource | null>(
  'lastViewedResource',
  null,
  {
    encode: JSON.stringify,
    decode: JSON.parse,
  },
);

export const $lastViewedCollection = persistentAtom<ILastViewedCollection | null>(
  'lastViewedCollection',
  null,
  {
    encode: JSON.stringify,
    decode: JSON.parse,
  },
);


export const updateLastViewedResource = (
  id: string,
  name: string,
  description: string,
  link: string,
  resourceType: IResourceProgress['resourceType'],
  progress?: {
    completed_lesson_count: number;
    total_lesson_count: number;
  },
  duration?: string,
) => {
  const lastViewedResource: ILastViewedResource = {
    id,
    name,
    description,
    link,
    resourceType,
    duration,
    progress: progress
      ? {
          ...progress,
          percentage: calculateCourseProgress(progress),
        }
      : undefined,
    lastViewedAt: Date.now(),
  };

  $lastViewedResource.set(lastViewedResource);
};

export const clearLastViewedResource = () => {
  $lastViewedResource.set(null);
};

export const updateLastViewedCollection = (
  collectionId: string,
  collectionName: string,
  collectionDescription: string,
  collectionLink: string,
  collectionProgress: {
    completed_course_count: number;
    total_course_count: number;
  } | undefined,
  courseId: string,
  courseName: string,
  courseDescription: string,
  courseLink: string,
  courseDuration?: string,
  courseProgress?: {
    completed_lesson_count: number;
    total_lesson_count: number;
  },
  collectionType?: ICollectionProgress['collectionType'],
) => {
  const lastViewedCollection: ILastViewedCollection = {
    id: collectionId,
    name: collectionName,
    description: collectionDescription,
    link: collectionLink,
    collectionType,
    progress: collectionProgress
      ? {
          ...collectionProgress,
          percentage: Math.round((collectionProgress.completed_course_count / collectionProgress.total_course_count) * 100),
        }
      : undefined,
    lastViewedCourse: {
      id: courseId,
      name: courseName,
      description: courseDescription,
      link: courseLink,
      duration: courseDuration,
      progress: courseProgress
        ? {
            ...courseProgress,
            percentage: calculateCourseProgress(courseProgress),
          }
        : undefined,
    },
    lastViewedAt: Date.now(),
  };

  $lastViewedCollection.set(lastViewedCollection);
};

export const clearLastViewedCollection = () => {
  $lastViewedCollection.set(null);
};

// Simple content pathname storage for login redirects
const $lastViewedContent = persistentAtom<string | null>('lastViewedContent', null, {
  encode: JSON.stringify,
  decode: JSON.parse,
});

export const setLastViewedContent = (pathname: string) => {
  $lastViewedContent.set(pathname);
};

export const getLastViewedContent = (): string | null => {
  return $lastViewedContent.get();
};

export const clearLastViewedContent = () => {
  $lastViewedContent.set(null);
};
